import initLazyVideo from '../../utilities/lazy-video';
import { lenis } from '../../global/gsap';

const initFilters = (wrapper) => {
    // Get active filter buttons and return their slugs as array
    const getActiveFilters = () => {
        const buttons = wrapper.querySelectorAll('.button[data-slug].active');
        return Array.from(buttons).map((btn) => btn.dataset.slug);
    };

    let urlSegment = '/projects/';

    if (document.body.dataset.page === 'projects-index') {
        urlSegment = '/projects/index/';
    }

    // Build URL using selected categories
    const buildUrl = (categories) => {
        const url = new URL(urlSegment, window.location.origin);

        if (categories.length) {
            url.searchParams.set('project-type', categories.join(','));
        }

        return url;
    };

    const content = document.querySelector('.js-filter-content');

    if (!content) return;

    let activeRequest = null;

    // Handles fetching and rendering filtered content
    const fetchResults = async (url) => {
        // Abort any in-flight fetch requests
        if (activeRequest) activeRequest.abort();
        activeRequest = new AbortController();

        // Add loading class
        document.body.classList.add('working');

        // Pass 'partial' param to avoid fetching entire page
        url.searchParams.set('partial', '1');

        try {
            const response = await fetch(url.toString(), { signal: activeRequest.signal });

            // Throw error if fetch response fails
            if (!response.ok) throw new Error(`Request failed: ${response.status}`);

            // Get html from fetch and parse it
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');

            // Update content with parsed HTML
            content.innerHTML = doc.querySelector('.js-filter-content').innerHTML;

            // Re-run SVG inlining for motif images in freshly rendered cards
            if (typeof window.bodhisvgsInlineSupport === 'function') {
                window.bodhisvgsInlineSupport();
            }

            // Init lazy videos in freshly rendered results
            const lazyVideos = [...content.querySelectorAll('video[data-src], video source[data-src]').values()].map((el) => el.closest('video'));
            const uniqueVideos = [...new Set(lazyVideos)];
            if (uniqueVideos.length) initLazyVideo(uniqueVideos);

            // Use Lenis scroll so it works with the site's smooth scroll library
            requestAnimationFrame(() => {
                lenis.scrollTo('.projects', { duration: 1 });
            });
        } catch (error) {
            if (error.name !== 'AbortError') console.error('Filtering failed:', error);
        } finally {
            // finally ensures the loading class is always removed regardless of success or failure
            document.body.classList.remove('working');
        }
    };

    const clearBtn = wrapper.querySelector('.js-filter-clear');

    const syncClearButton = () => {
        const hasActive = wrapper.querySelector('.button[data-slug].active') !== null;
        clearBtn?.classList.toggle('is-visible', hasActive);
    };

    // Update button active states based on query params
    const syncButtons = (url) => {
        const active = url.searchParams.get('project-type');
        const activeCategories = active ? active.split(',') : [];

        wrapper.querySelectorAll('.button[data-slug]').forEach((btn) => {
            btn.classList.toggle('active', activeCategories.includes(btn.dataset.slug));
        });

        syncClearButton();
    };

    // Sync clear button visibility on init
    syncClearButton();

    // Listener for filter button clicks
    wrapper.addEventListener('click', async (event) => {
        const filterBtn = event.target.closest('.button[data-slug]');

        if (filterBtn) {
            filterBtn.classList.toggle('active');
            syncClearButton();
            const categories = getActiveFilters();
            const url = buildUrl(categories);
            history.pushState(null, '', url.toString());
            await fetchResults(url);
        } else if (event.target.closest('.js-filter-clear')) {
            wrapper.querySelectorAll('.button[data-slug].active').forEach((btn) => {
                btn.classList.remove('active');
            });
            syncClearButton();
            const url = buildUrl([]);
            history.pushState(null, '', url.toString());
            await fetchResults(url);
        }
    });

    window.addEventListener('popstate', async () => {
        const url = new URL(window.location.href);
        syncButtons(url);
        await fetchResults(url);
    });

    // Mobile filter toggle logic
    const toggle = wrapper.querySelector('.js-filter-toggle');

    if (toggle) {
        let filtersOpen = false;

        const closeFilters = () => {
            filtersOpen = false;
            document.body.classList.remove('filters-open');
            toggle.setAttribute('aria-expanded', false);
        };

        toggle.addEventListener('click', (event) => {
            event.preventDefault();
            filtersOpen = !filtersOpen;
            document.body.classList.toggle('filters-open', filtersOpen);
            toggle.setAttribute('aria-expanded', filtersOpen);
        });

        document.addEventListener('click', (event) => {
            if (filtersOpen && !wrapper.contains(event.target)) {
                closeFilters();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (filtersOpen && event.key === 'Escape') {
                closeFilters();
                toggle.focus();
            }
        });
    }
};

export default initFilters;
